import React, { useState, useEffect, useMemo } from 'react';
import { Checkbox, Chip, CircularProgress, FormControlLabel, Icon, IconButton, ListSubheader, MenuItem, TextField, Typography } from '@material-ui/core';
import clsx from 'clsx';
import axios from 'axios';
import { API_BASE_URL } from '@config';
import { useSelector } from 'react-redux';
import useStyles from 'app/cluster-styles/useStyles';
import { IMAGE_TYPE, IMAGE_MODE } from '../../util/enums';
import SectionCard from './SectionCard';
import UnavailableReason, { DIMMED } from './UnavailableReason';

// Cloud providers get the Heidi Desktop default (on, DCV off) for Heidi-Desktop-capable
// images (Ubuntu 26 + Rocky/RHEL 9).
const CLOUD_PROVIDERS = ['aws', 'azure', 'gcp', 'ibm', 'oracle'];

// Captured/base image names end with the unix-seconds timestamp they were created at
// ("<prefix>-heidi-<seconds>"), so we can derive a date for any image - even base/cloud
// ones that carry no stored createdAt - straight from the name.
const tsFromName = (name) => {
    const m = /-(\d{9,})$/.exec(name || '');
    return m ? parseInt(m[1], 10) * 1000 : 0;
};
const versionMs = (v) => (v && v.createdAt ? new Date(v.createdAt).getTime() : 0) || tsFromName(v && v.name);
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '');

const ENABLED = { disabled: false, reason: null };
const chipStyle = { background: '#e0f2fe', color: '#075985' };

/**
 * Find the stack an existing cluster's image_name came from.
 *
 * A cluster stores only the stack PREFIX in server.image_name (the specific image is
 * pinned separately by id), and a prefix is NOT unique across stacks - nothing stops two
 * stacks claiming "core". Resolving that with a bare find() returned whichever stack
 * happened to sort first, and the caller then took ITS data.type as the cluster's OS.
 * That is how a rocky-9 cluster ended up with os-type ubuntu-22.04, ssh_username 'ubuntu'
 * and a deploy that failed on SSH against a Rocky box.
 *
 * Ambiguity cannot be removed here - the stored name genuinely does not say which stack -
 * so make the choice deterministic and as right as the data allows:
 *   1. exact prefix match beats a "<prefix>-..." match
 *   2. the longest prefix wins (a stack "core-gpu" beats "core" for "core-gpu-heidi-123")
 *   3. among what is left, prefer a stack whose type matches the OS already on the form
 *   4. finally, stable by id so repeated renders agree with each other
 * Picking a stack from the menu does not go through here at all - that path resolves by
 * stack id, which is exact.
 */
export function stackForImageName(stacks, imageName, osType) {
    const name = imageName || '';
    if (!name) return undefined;
    const candidates = (stacks || []).filter((s) => {
        const p = s && s.data && s.data.prefix;
        return p && (name === p || name.indexOf(p + '-') === 0);
    });
    if (candidates.length <= 1) return candidates[0];
    const score = (s) => {
        const p = s.data.prefix;
        return [
            name === p ? 1 : 0,
            p.length,
            osType && s.data.type === osType ? 1 : 0,
        ];
    };
    return candidates.slice().sort((a, b) => {
        const sa = score(a);
        const sb = score(b);
        for (let i = 0; i < sa.length; i += 1) {
            if (sa[i] !== sb[i]) return sb[i] - sa[i];
        }
        return String(a.id) < String(b.id) ? -1 : 1;
    })[0];
}

/**
 * Feature: image-customization - standalone, selection-only image picker (IBM, AWS).
 * Rendered next to the Infrastructure card so the user picks region + credential first,
 * then the image. Two groups: Heidi "Stacks" (by prefix/version) and "Cloud images"
 * (Heidi images discovered in the account, pinned by explicit id). No free text -> no typos.
 *
 * With only the base props the picker lists the provider's built stacks and fetches its
 * cloud images from the infrastructure-verification service (IBM). A provider with its own
 * live image data (AWS) passes the optional props instead:
 *   stacks                      stack records to list (default: built stacks for the provider)
 *   getStackOptionState(stack)  -> { disabled, reason } shown inline on the row
 *   renderStackExtras(stack)    replaces the default type chip
 *   cloudImages                 [{ id, name, os, arch, osType, ... }]; skips the internal fetch
 *   cloudImagesLoading, cloudImagesPlaceholder
 *   getCloudImageOptionState(img), renderCloudImageExtras(img)
 *   onDeleteCloudImage(img)     adds a delete action to each cloud image row
 *   imageNameFor(img)           image name stored for a cloud pick (default: img.name)
 *   emptyLabel, helperText, error
 */
export default function ImageSelectionSection({
    form, setField, provider, disableStackFields,
    stacks: stacksProp, getStackOptionState, renderStackExtras,
    cloudImages: cloudImagesProp, cloudImagesLoading = false, cloudImagesPlaceholder,
    getCloudImageOptionState, renderCloudImageExtras, onDeleteCloudImage,
    imageNameFor, emptyLabel = 'Select an image', helperText, error = false,
}) {
    const classes = useStyles();
    const reduxStacks = useSelector(({ stacks }) => stacks.stack.list) || [];
    const stacks = stacksProp || reduxStacks;

    const providerStacks = stacksProp || reduxStacks.filter(
        (s) => s.info && s.info.state === 'success' && s.provider === provider
    );
    const liveCloudImages = Array.isArray(cloudImagesProp);

    const osType = form.osType || '';
    const serverImageName = form.serverImageName || '';
    const serverImageId = form.serverImageId || '';
    const serverImageType = form.serverImageType || IMAGE_TYPE.HEIDI_STACK;

    // IBM and AWS are unified-only: no Image Mode choice. Force it so the deploy uses the
    // unified template/image resolution regardless of any stale default.
    useEffect(() => {
        if (form.imageMode !== IMAGE_MODE.UNIFIED) setField('imageMode', IMAGE_MODE.UNIFIED);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.imageMode]);

    const [fetchedCloudImages, setFetchedCloudImages] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [versions, setVersions] = useState([]);
    const [versionStackId, setVersionStackId] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);

    // Cloud images for the selected region/credential (reuses the CLI-based service).
    // No arch filter here: the OS field is gone, so osType is DERIVED from the chosen
    // image rather than driving the list.
    useEffect(() => {
        if (liveCloudImages) return;
        if (!form.regionId || !form.credentialId) { setFetchedCloudImages([]); setFetchLoading(false); return; }
        setFetchLoading(true);
        const params = new URLSearchParams({
            provider, region: form.regionId, credentialId: form.credentialId
        });
        axios.get(`${API_BASE_URL}/api/instance-availability/images?${params.toString()}`)
            .then((r) => setFetchedCloudImages((r.data && r.data.images) || []))
            .catch(() => setFetchedCloudImages([]))
            .finally(() => setFetchLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveCloudImages, provider, form.regionId, form.credentialId]);

    const cloudImages = liveCloudImages ? cloudImagesProp : fetchedCloudImages;
    const cloudLoading = liveCloudImages ? cloudImagesLoading : fetchLoading;

    const stackState = (stack) => (getStackOptionState ? getStackOptionState(stack) : ENABLED);
    const cloudState = (img) => (getCloudImageOptionState ? getCloudImageOptionState(img) : ENABLED);

    // Resolve the osType for a cloud image. Prefer the value we stored at capture time
    // (in the stack's imageVersions) over IBM's metadata, which can be stale (a captured
    // 26.04 image still reports 24.04). Falls back to the endpoint's mapped osType.
    const resolveCloudOsType = (img) => {
        for (let s = 0; s < providerStacks.length; s++) {
            const vers = providerStacks[s].imageVersions || [];
            const hit = vers.find((v) => v.imageId === img.id);
            if (hit && hit.osType) return hit.osType;
        }
        return img.osType || '';
    };

    // Captured versions for the stack matching the current image name.
    useEffect(() => {
        const match = stackForImageName(providerStacks, serverImageName, osType);
        if (match && match.id) {
            if (match.id !== versionStackId) {
                setVersionStackId(match.id);
                axios.get(`${API_BASE_URL}/api/stacks/${match.id}/versions`)
                    .then((r) => {
                        const data = r.data || [];
                        setVersions(data);
                        // Pre-select the latest version rather than the generic "Default
                        // (newest)" sentinel. Pin by image id (-> unified_image_id) so the
                        // deploy resolves it; the name stays the stack prefix. Only when the
                        // image is still unpinned, so we never override a choice on an edit.
                        if (data.length && !serverImageId && serverImageName === match.data.prefix) {
                            const newest = data.slice().sort((a, b) => versionMs(b) - versionMs(a))[0];
                            if (newest && newest.imageId) setField('serverImageId', newest.imageId);
                        }
                    })
                    .catch(() => setVersions([]));
            }
        } else if (versionStackId) {
            setVersionStackId(null);
            setVersions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverImageName, stacks]);

    const matchedStack = stackForImageName(providerStacks, serverImageName, osType);
    // A matched stack means the image_name is a stack prefix - show the stack as selected
    // even when a specific version is pinned via serverImageId (the version pin keeps the
    // name = prefix). Only fall back to "cloud:<id>" for a cloud image picked directly.
    // Keyed on the stack ID, not the prefix: prefixes are not unique (see
    // stackForImageName), and two stacks sharing one made the Select round-trip to
    // whichever happened to be first in the list.
    const imageSelectValue = matchedStack
        ? `stack:${matchedStack.id}`
        : (serverImageId ? `cloud:${serverImageId}` : '');

    // Prefixes that more than one built stack claims. Shown on the offending rows so the
    // ambiguity is visible at the point of choosing, instead of surfacing minutes into a
    // deploy as an SSH failure against the wrong OS's default user.
    const duplicatePrefixes = useMemo(() => {
        const seen = Object.create(null);
        const dupes = new Set();
        providerStacks.forEach((s) => {
            const p = s.data && s.data.prefix;
            if (!p) return;
            if (seen[p]) dupes.add(p);
            seen[p] = true;
        });
        return dupes;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerStacks]);
    const selectedStack = providerStacks.find((s) => s.id === versionStackId);

    // An instance can never boot from a disk smaller than the image it was built from -
    // IBM sizes the boot volume to exactly the image's minimum when Terraform doesn't ask
    // for a size. Resolve that floor for the selected image and publish it on the form so
    // Storage Settings can enforce it instead of letting the user type a size that is
    // silently ignored. Mirrors deploy-time resolution: a pinned id wins, otherwise the
    // newest "<prefix>-heidi-<ts>". null = unknown (no region/credential yet, or a cached
    // pre-upgrade list), which simply means "no extra floor".
    //
    // The prefix falls back to serverImageName rather than requiring a matched stack: an
    // existing cluster can carry a stack prefix whose Stack record is gone (deleted, or
    // built on another box) while its images are still in the account. Keying off the
    // stack alone left those clusters with no floor at all, which is exactly the silent
    // mis-sizing this fix exists to prevent. init.js resolves the same way, from
    // config.server.image_name.
    const matchedStackPrefix = (matchedStack && matchedStack.data && matchedStack.data.prefix) || '';
    const minSizePrefix = matchedStackPrefix || serverImageName;
    const minSizeImage = useMemo(() => {
        if (!cloudImages.length) return null;
        if (serverImageId) return cloudImages.find((i) => i.id === serverImageId) || null;
        if (!minSizePrefix) return null;
        return cloudImages
            .filter((i) => i.name && i.name.indexOf(`${minSizePrefix}-heidi-`) === 0)
            .sort((a, b) => versionMs(b) - versionMs(a))[0] || null;
    }, [cloudImages, serverImageId, minSizePrefix]);

    const imageMinSizeGb = (minSizeImage && minSizeImage.minSizeGb) || null;
    const imageMinSizeFrom = (imageMinSizeGb && minSizeImage.name) || '';

    useEffect(() => {
        if ((form.imageMinSizeGb ?? null) !== imageMinSizeGb) setField('imageMinSizeGb', imageMinSizeGb);
        if ((form.imageMinSizeFrom || '') !== imageMinSizeFrom) setField('imageMinSizeFrom', imageMinSizeFrom);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageMinSizeGb, imageMinSizeFrom]);

    // The captured versions live in imageVersions[] (with a friendly displayName). The
    // stack's original packer-built image is NOT recorded there, so surface it as a
    // "Base" entry: any cloud image that matches the stack prefix and isn't one of the
    // recorded captures. Lets the user roll all the way back to the unmodified image.
    const stackPrefix = selectedStack && selectedStack.data && selectedStack.data.prefix;
    const capturedIds = new Set(versions.map((v) => v.imageId));
    const baseImages = stackPrefix
        ? cloudImages.filter((img) =>
            img && img.name && !capturedIds.has(img.id) &&
            (img.name === stackPrefix || img.name.indexOf(stackPrefix + '-') === 0))
        : [];

    // Versions/base entries pin by image id (-> unified_image_id). "__default__" = newest
    // by prefix (no pin).
    const versionOptionIds = versions.map((v) => v.imageId).concat(baseImages.map((b) => b.id));
    const versionSelectValue = versionOptionIds.indexOf(serverImageId) !== -1 ? serverImageId : '__default__';

    // On a cloud provider, a Heidi-Desktop-capable image defaults the Remote Desktop to
    // Heidi Desktop with DCV off (other OSes are left untouched). That is Ubuntu 26 AND
    // Rocky/RHEL 9 (x86_64) — the nodus.desktop role supports both (Rocky 9 builds the
    // wlroots stack from source; see redhat-9.yml). Detect from the resolved osType OR the
    // image name/description, because some cloud base images report stale OS metadata (a
    // captured 26.04 image can come back as ubuntu-24-04). This runs here, at selection
    // time, because the Remote Desktop card only mounts in advanced mode -- but osType is
    // defined the moment an image is picked, in either mode.
    const applyHeidiDesktopDefault = (osType, label) => {
        if (CLOUD_PROVIDERS.indexOf(provider) === -1) return;
        const hay = ((osType || '') + ' ' + (label || '')).toLowerCase();
        const isUbuntu26 = hay.indexOf('ubuntu-26') !== -1 || hay.indexOf('ubuntu 26') !== -1 || /\b26[.-]04\b/.test(hay);
        const isRocky9 = hay.indexOf('rocky-9') !== -1 || hay.indexOf('rocky 9') !== -1
            || /\brocky[ ._-]?linux[ ._-]?9\b/.test(hay) || /\brhel[ ._-]?9\b/.test(hay);
        if (!isUbuntu26 && !isRocky9) return;
        setField('dcvEnabled', false);
        setField('defaultRemoteProtocol', 'desktop');
    };

    const handleImageSelect = (val) => {
        if (val && val.indexOf('stack:') === 0) {
            // Resolve by stack ID. This used to look the stack up by its prefix, but a
            // prefix is not unique: with two stacks sharing one, find() returned whichever
            // came first and the cluster silently took THAT stack's data.type as its OS.
            // Picking a rocky-9 stack whose prefix collided with an older ubuntu stack
            // wrote os-type ubuntu-22.04, which derives ssh_username 'ubuntu', and the
            // deploy died 13 minutes later on "SSH authentication failed (ubuntu@...)"
            // against a Rocky box.
            const stackId = val.slice(6);
            const stack = providerStacks.find((s) => String(s.id) === stackId);
            // MUI still fires onChange for a row it renders disabled; refuse it here too.
            if (!stack || stackState(stack).disabled) return;
            const prefix = stack.data && stack.data.prefix;
            setField('serverImageId', '');
            setField('serverImageName', prefix);
            // Derive OS from the stack (its data.type is the source of truth).
            const osType = stack && stack.data && stack.data.type;
            if (osType) setField('osType', osType);
            applyHeidiDesktopDefault(osType, (stack && (stack.displayName || stack.name)) || prefix);
        } else if (val && val.indexOf('cloud:') === 0) {
            const id = val.slice(6);
            const img = cloudImages.find((i) => i.id === id);
            if (img && cloudState(img).disabled) return;
            setField('serverImageId', id);
            setField('serverImageName', img ? (imageNameFor ? imageNameFor(img) : img.name) : id);
            // Derive OS from the image (prefer stored capture-time osType over IBM metadata).
            // Live image lists (AWS) often cannot tell the OS; keep the user's choice then.
            const osType = img ? resolveCloudOsType(img) : '';
            if (img && (osType || !liveCloudImages)) setField('osType', osType);
            applyHeidiDesktopDefault(osType, img ? ((img.name || '') + ' ' + (img.os || '')) : '');
        }
    };

    // Every row of both groups shares one set of columns, so names, chips, reasons and the
    // delete action line up down the whole menu. The list is the grid; each row takes its
    // columns from it (subgrid), so a column is as wide as its widest entry. The reason and
    // action columns exist only when some row can be disabled or deleted.
    const hasReasonColumn = Boolean(getStackOptionState || getCloudImageOptionState);
    const hasActionColumn = Boolean(onDeleteCloudImage);
    const menuColumns = ['minmax(0, 1fr)', 'auto']
        .concat(hasReasonColumn ? ['auto'] : [], hasActionColumn ? ['auto'] : [])
        .join(' ');
    const spanAll = { gridColumn: '1 / -1' };
    const rowStyle = { ...spanAll, display: 'grid', gridTemplateColumns: 'subgrid', alignItems: 'center' };
    const cellStyle = { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 };

    // A disabled row dims its name and chips but not its reason: the reason is the one part
    // of that row the user needs to read. MUI fades the whole disabled item by default, so
    // the item keeps full opacity and the cells fade themselves.
    const optionRow = ({ key, value, label, extras, state, onDelete, deleteLabel }) => (
        <MenuItem key={key} value={value} disabled={state.disabled} style={{ ...rowStyle, opacity: 1 }}>
            <span style={state.disabled ? { ...cellStyle, ...DIMMED } : cellStyle}>{label}</span>
            <span style={state.disabled ? { ...cellStyle, ...DIMMED } : cellStyle}>{extras}</span>
            {hasReasonColumn && (
                <span style={cellStyle}>
                    {state.disabled ? <UnavailableReason reason={state.reason} /> : null}
                </span>
            )}
            {hasActionColumn && (
                <span style={cellStyle}>
                    {onDelete && (
                        <IconButton
                            size="small"
                            aria-label={deleteLabel}
                            title={deleteLabel}
                            style={{ pointerEvents: 'auto' }}
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(); }}
                        >
                            <Icon fontSize="small">delete_outline</Icon>
                        </IconButton>
                    )}
                </span>
            )}
        </MenuItem>
    );

    return (
        <SectionCard icon="layers" title="Image">
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <TextField
                    select
                    label="Image"
                    className={clsx(classes.input)}
                    style={{ flex: 1 }}
                    required
                    disabled={disableStackFields}
                    value={imageSelectValue}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    // Force the label to float so it doesn't overlap the "Select an image"
                    // placeholder when nothing is selected yet.
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                        displayEmpty: true,
                        open: menuOpen,
                        onOpen: () => setMenuOpen(true),
                        onClose: () => setMenuOpen(false),
                        MenuProps: {
                            PaperProps: { style: { maxWidth: 'min(1000px, 94vw)' } },
                            MenuListProps: { style: { display: 'grid', gridTemplateColumns: menuColumns, columnGap: 16 } },
                        },
                        renderValue: (v) => {
                            if (!v) return <span style={{ color: '#94a3b8' }}>{emptyLabel}</span>;
                            if (v.indexOf('stack:') === 0) {
                                // The value is the stack ID now, so map it back to something
                                // readable rather than printing a mongo id at the user.
                                const st = providerStacks.find((s) => `stack:${s.id}` === v);
                                return st ? (st.data && st.data.prefix) || st.displayName || st.name : v.slice(6);
                            }
                            const img = cloudImages.find((i) => `cloud:${i.id}` === v);
                            return img ? img.name : v.slice(6);
                        },
                    }}
                    onChange={(e) => handleImageSelect(e.target.value)}
                >
                    <ListSubheader style={spanAll}>Stacks</ListSubheader>
                    {providerStacks.length > 0
                        ? providerStacks.map((stack) => optionRow({
                            key: `stack:${stack.id}`,
                            value: `stack:${stack.id}`,
                            label: (
                                <>
                                    {stack.displayName || stack.name}
                                    <span style={{ color: '#94a3b8', marginLeft: 6 }}>({stack.data.prefix})</span>
                                    {duplicatePrefixes.has(stack.data.prefix) && (
                                        <span
                                            style={{ color: '#b45309', marginLeft: 6 }}
                                            title={`Another stack also uses the prefix "${stack.data.prefix}". Deploys resolve the image by prefix, so give one of them a unique prefix.`}
                                        >
                                            (shared prefix)
                                        </span>
                                    )}
                                </>
                            ),
                            extras: renderStackExtras
                                ? renderStackExtras(stack)
                                : (stack.data && stack.data.type && (
                                    <Chip size="small" style={chipStyle} label={stack.data.type} />
                                )),
                            state: stackState(stack),
                        }))
                        : <MenuItem disabled style={spanAll}>No stacks built for this provider</MenuItem>}
                    <ListSubheader style={spanAll}>Cloud images</ListSubheader>
                    {cloudLoading
                        ? <MenuItem disabled style={spanAll}>
                            <CircularProgress size={16} className="mr-8" />
                            Loading cloud images...
                        </MenuItem>
                        : cloudImages.length > 0
                            ? cloudImages.map((img) => optionRow({
                                key: `cloud:${img.id}`,
                                value: `cloud:${img.id}`,
                                label: img.name,
                                extras: renderCloudImageExtras
                                    ? renderCloudImageExtras(img)
                                    : <Chip size="small" style={chipStyle} label={img.os ? `${img.os}` : (img.arch || 'image')} />,
                                state: cloudState(img),
                                onDelete: onDeleteCloudImage ? () => onDeleteCloudImage(img) : null,
                                deleteLabel: `Delete image ${img.name}`,
                            }))
                            : <MenuItem disabled style={spanAll}>{cloudImagesPlaceholder || (form.regionId && form.credentialId ? 'No cloud images found' : 'Select region + credential first')}</MenuItem>}
                </TextField>

                <FormControlLabel
                    style={{ marginTop: '4px', width: '180px' }}
                    control={
                        <Checkbox
                            color="primary"
                            disabled={disableStackFields}
                            checked={serverImageType === IMAGE_TYPE.CUSTOM_STACK}
                            onChange={(e) => setField('serverImageType', e.target.checked ? IMAGE_TYPE.CUSTOM_STACK : IMAGE_TYPE.HEIDI_STACK)}
                        />
                    }
                    label={
                        <span>
                            Custom image
                            <Typography variant="caption" color="textSecondary" display="block">
                                Leave off for a Heidi stack
                            </Typography>
                        </span>
                    }
                />
            </div>

            {selectedStack && (versions.length > 0 || baseImages.length > 0) && (
                <div style={{ marginTop: '16px' }}>
                    <TextField
                        select
                        label="Image Version"
                        className={clsx(classes.input)}
                        style={{ width: '420px' }}
                        disabled={disableStackFields}
                        value={versionSelectValue}
                        variant="outlined"
                        helperText="Pick an older version to roll back; default uses the newest."
                        onChange={(e) => {
                            const val = e.target.value;
                            // Always keep the image name as the stack prefix; the specific
                            // image is pinned by id (val) so the unified template resolves it.
                            if (selectedStack) setField('serverImageName', selectedStack.data.prefix);
                            setField('serverImageId', val === '__default__' ? '' : val);
                        }}
                    >
                        <MenuItem value="__default__">Default (newest)</MenuItem>
                        {versions.map((v) => {
                            // Captures carry a friendly displayName; the packer base image
                            // (source 'packer') is labelled "Base". A legacy capture with no
                            // name just shows its image name rather than masquerading as Base.
                            const label = v.source === 'packer' ? 'Base' : (v.displayName || '');
                            const date = fmtDate(versionMs(v));
                            return (
                                <MenuItem key={v._id || v.imageId} value={v.imageId}>
                                    <span style={{ flex: 1 }}>
                                        {label ? <strong>{label}</strong> : null}
                                        <span style={{ color: '#94a3b8', marginLeft: label ? 6 : 0 }}>{v.name}</span>
                                    </span>
                                    {date ? <span style={{ color: '#94a3b8', marginLeft: 12, fontSize: 12 }}>{date}</span> : null}
                                    {v.isDefault ? <span style={{ color: '#94a3b8', marginLeft: 6 }}>(default)</span> : null}
                                </MenuItem>
                            );
                        })}
                        {baseImages.map((img) => {
                            const date = fmtDate(tsFromName(img.name));
                            return (
                                <MenuItem key={img.id} value={img.id}>
                                    <span style={{ flex: 1 }}>
                                        Base
                                        <span style={{ color: '#94a3b8', marginLeft: 6 }}>{img.name}</span>
                                    </span>
                                    {date ? <span style={{ color: '#94a3b8', marginLeft: 12, fontSize: 12 }}>{date}</span> : null}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                </div>
            )}

        </SectionCard>
    );
}
